import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    DTOApplicationFormSchema,
    DTOApplicationFormData,
    MLTAApplicationFormSchema,
    MLTAApplicationFormData,
    GIWLApplicationFormData,
    GIWLApplicationFormSchema,
} from "types/Card";

/**
 * Application form hook using React Hook Form + Zod
 * Manages state for all card components within a single form.
 * Used for both DTO and MLTA products on shared pages (ContactInformation, etc.)
 *
 * @param initialValues - Optional pre-filled values for resuming a form
 */
export function useApplicationForm(initialValues?: Partial<DTOApplicationFormData>) {
    const defaultValues: DTOApplicationFormData = {
        coverageReplacement: {
            isReplacing: null,
            replacementCompany: "",
            replacementAccount: "",
        },
        statementOfHealth: {
            height: {
                feet: "",
                inches: "",
            },
            weight: "",
            hasUsedNicotine: false, // Default to false, will be overridden by Redux state
            hasMedicalConditions: null,
            hasDiagnosticTesting: null,
        },
        contactInformation: {
            applicantType: "member",
            firstName: "",
            lastName: "",
            dateOfBirth: {
                month: "",
                day: "",
                year: "",
            },
            gender: undefined,
            email: "",
            phone: "",
            phoneType: "" as any,
            address: "",
            city: "",
            state: "",
            zipCode: "",
            aaaMemberNumber: undefined,
        },
        policyBeneficiary: {
            firstName: "",
            lastName: "",
            relationship: "",
        },
        secondaryAddressee: {
            hasSecondaryAddressee: null,
            firstName: "",
            lastName: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            phone: "",
        },
        quote: {
            applicantType: "member",
            dateOfBirth: {
                month: "",
                day: "",
                year: "",
            },
            gender: "" as any,
            state: "",
            nicotineUsage: "",
            coverageAmount: 0,
            coveragePremium: 0,
        },
    };

    // Merge initial values with defaults, with initial values taking precedence
    const mergedValues: DTOApplicationFormData = {
        ...defaultValues,
        ...initialValues,
        coverageReplacement: {
            ...defaultValues.coverageReplacement,
            ...initialValues?.coverageReplacement,
        },
        statementOfHealth: {
            ...defaultValues.statementOfHealth,
            ...initialValues?.statementOfHealth,
            height: {
                ...defaultValues.statementOfHealth.height,
                ...initialValues?.statementOfHealth?.height,
            },
        },
        contactInformation: {
            ...defaultValues.contactInformation,
            ...initialValues?.contactInformation,
            dateOfBirth: {
                ...defaultValues.contactInformation.dateOfBirth,
                ...initialValues?.contactInformation?.dateOfBirth,
            },
        },
        policyBeneficiary: {
            ...defaultValues.policyBeneficiary,
            ...initialValues?.policyBeneficiary,
        },
        secondaryAddressee: {
            ...defaultValues.secondaryAddressee,
            ...initialValues?.secondaryAddressee,
        },
        quote: {
            ...defaultValues.quote,
            ...initialValues?.quote,
            dateOfBirth: {
                ...defaultValues.quote.dateOfBirth,
                ...initialValues?.quote?.dateOfBirth,
            },
        },
    };

    return useForm<DTOApplicationFormData>({
        resolver: zodResolver(DTOApplicationFormSchema),
        defaultValues: mergedValues,
        mode: "onBlur",
    });
}

/**
 * MLTA application form hook using React Hook Form + Zod
 * Uses MLTA-specific schema with required zipCode field
 *
 * @param initialValues - Optional pre-filled values for resuming a form
 */
export function useMLTAApplicationForm(initialValues?: Partial<MLTAApplicationFormData>) {
    const defaultValues: MLTAApplicationFormData = {
        contactInformation: {
            applicantType: "member",
            firstName: "",
            lastName: "",
            dateOfBirth: {
                month: "",
                day: "",
                year: "",
            },
            gender: undefined,
            email: "",
            phone: "",
            phoneType: "" as any,
            address: "",
            city: "",
            state: "",
            zipCode: "",
            aaaMemberNumber: undefined,
        },
        policyBeneficiary: {
            firstName: "",
            lastName: "",
            relationship: "",
        },
        secondaryAddressee: {
            hasSecondaryAddressee: null,
            firstName: "",
            lastName: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            phone: "",
        },
        quote: {
            dateOfBirth: {
                month: "",
                day: "",
                year: "",
            },
            state: "",
            coverageAmount: 0,
            coveragePremium: 0,
            zipCode: "",
        },
    };

    // Merge initial values with defaults
    const mergedValues: MLTAApplicationFormData = {
        ...defaultValues,
        ...initialValues,
        contactInformation: {
            ...defaultValues.contactInformation,
            ...initialValues?.contactInformation,
            dateOfBirth: {
                ...defaultValues.contactInformation.dateOfBirth,
                ...initialValues?.contactInformation?.dateOfBirth,
            },
        },
        policyBeneficiary: {
            ...defaultValues.policyBeneficiary,
            ...initialValues?.policyBeneficiary,
        },
        secondaryAddressee: {
            ...defaultValues.secondaryAddressee,
            ...initialValues?.secondaryAddressee,
        },
        quote: {
            ...defaultValues.quote,
            ...initialValues?.quote,
            dateOfBirth: {
                ...defaultValues.quote.dateOfBirth,
                ...initialValues?.quote?.dateOfBirth,
            },
        },
    };

    return useForm<MLTAApplicationFormData>({
        resolver: zodResolver(MLTAApplicationFormSchema),
        defaultValues: mergedValues,
        mode: "onBlur",
    });
}

/**
 * GIWL application form hook using React Hook Form + Zod
 * Uses GIWL-specific schema with required zipCode field
 *
 * @param initialValues - Optional pre-filled values for resuming a form
 */
export function useGIWLApplicationForm(initialValues?: Partial<GIWLApplicationFormData>) {
    const defaultValues: GIWLApplicationFormData = {
        coverageReplacement: {
            isReplacing: null,
            replacementCompany: "",
            replacementAccount: "",
        },
        contactInformation: {
            applicantType: "member",
            firstName: "",
            lastName: "",
            dateOfBirth: {
                month: "",
                day: "",
                year: "",
            },
            gender: undefined,
            email: "",
            phone: "",
            phoneType: "" as any,
            address: "",
            city: "",
            state: "",
            zipCode: "",
            aaaMemberNumber: undefined,
        },
        policyBeneficiary: {
            firstName: "",
            lastName: "",
            relationship: "",
        },
        secondaryAddressee: {
            hasSecondaryAddressee: null,
            firstName: "",
            lastName: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            phone: "",
        },
        quote: {
            dateOfBirth: {
                month: "",
                day: "",
                year: "",
            },
            state: "",
            coverageAmount: 0,
            coveragePremium: 0,
            zipCode: "",
        },
    };

    // Merge initial values with defaults
    const mergedValues: GIWLApplicationFormData = {
        ...defaultValues,
        ...initialValues,
        coverageReplacement: {
            ...defaultValues.coverageReplacement,
            ...initialValues?.coverageReplacement,
        },
        contactInformation: {
            ...defaultValues.contactInformation,
            ...initialValues?.contactInformation,
            dateOfBirth: {
                ...defaultValues.contactInformation.dateOfBirth,
                ...initialValues?.contactInformation?.dateOfBirth,
            },
        },
        policyBeneficiary: {
            ...defaultValues.policyBeneficiary,
            ...initialValues?.policyBeneficiary,
        },
        secondaryAddressee: {
            ...defaultValues.secondaryAddressee,
            ...initialValues?.secondaryAddressee,
        },
        quote: {
            ...defaultValues.quote,
            ...initialValues?.quote,
            dateOfBirth: {
                ...defaultValues.quote.dateOfBirth,
                ...initialValues?.quote?.dateOfBirth,
            },
        },
    };

    return useForm<GIWLApplicationFormData>({
        resolver: zodResolver(GIWLApplicationFormSchema),
        defaultValues: mergedValues,
        mode: "onBlur",
    });
}
